import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

const FirstTimeSetup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email -> token -> webauthn
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 🔹 Request Token for Email Verification
  const requestToken = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/auth/request-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send token');
      }

      const data = await response.json();
      if (data.setupRequired) {
        setToken(data.tempToken);
        setStep('webauthn');
        await setupWebAuthn();
      } else {
        setStep('token');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handle Email Token Verification
  const verifyToken = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
  
    try {
      const response = await fetch(`${API_URL}/auth/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token })
      });
  
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to verify token');
      }
  
      const data = await response.json();
  
      // If WebAuthn setup is required, redirect to WebAuthn
      if (data.setupRequired) {
        console.log("🚀 WebAuthn setup required, proceeding...");
        setStep('webauthn');
        await setupWebAuthn();
        return;
      }
  
      // Otherwise, log in the user
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate(data.user.role === 'user' ? '/employee_forms' : '/dashboard');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // 🔹 Handle WebAuthn Login
  const handleWebAuthnLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/auth/webauthn/login-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }) // Send email to backend
      });

      const data = await response.json();

      // 🔐 Require email verification if needed
      if (data.reauthRequired) {
        console.log("🔐 Re-authentication required. Redirecting to email verification.");
        setStep('token');
        return;
      }

      // ✅ Proceed with WebAuthn authentication
      await performWebAuthnLogin(data);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  const setupWebAuthn = async () => {
    if (!window.PublicKeyCredential) {
      setError('WebAuthn is not supported in this browser');
      return;
    }
  
    try {
      // Get registration options from server
      const optionsResponse = await fetch(`${API_URL}/auth/webauthn/register-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
  
      if (!optionsResponse.ok) {
        const data = await optionsResponse.json();
        throw new Error(data.error || 'Failed to get registration options');
      }
  
      const options = await optionsResponse.json();
  
      // 🔹 Enforce FaceID / TouchID (platform authenticator, no USB)
      options.authenticatorSelection = {
        authenticatorAttachment: "platform", // Enforce built-in authentication
        userVerification: "required", // Require FaceID, TouchID, etc.
        requireResidentKey: false
      };
  
      // Convert challenge and user.id from base64URL to Uint8Array
      options.challenge = Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
      options.user.id = Uint8Array.from(atob(options.user.id.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
  
      // Create credentials
      const credential = await navigator.credentials.create({ publicKey: options });
  
      // Convert response to base64URL string
      const attestationResponse = {
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId)))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=/g, ''),
        response: {
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, ''),
          attestationObject: btoa(String.fromCharCode(...new Uint8Array(credential.response.attestationObject)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '')
        },
        type: credential.type
      };
  
      // Send to server for verification
      const verifyResponse = await fetch(`${API_URL}/auth/webauthn/register-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, attestationResponse })
      });
  
      if (!verifyResponse.ok) {
        const data = await verifyResponse.json();
        throw new Error(data.error || 'Failed to verify WebAuthn registration');
      }
  
      const { token: authToken, user: userData } = await verifyResponse.json();
      
      // Store authentication details
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify({
        ...userData,
        webauthnEnabled: true,
        isAuthenticated: true
      }));
      
      navigate(userData.role === 'user' ? '/employee_forms' : '/dashboard');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  const performWebAuthnLogin = async (data) => {
    try {
      // Convert base64URL challenge to Uint8Array
      const challenge = Uint8Array.from(
        atob(data.challenge.replace(/-/g, '+').replace(/_/g, '/')), 
        c => c.charCodeAt(0)
      );
  
      // WebAuthn authentication request
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: challenge,
          timeout: 60000,
          rpId: process.env.NODE_ENV === 'development' ? 'localhost' : 'daileyhr.com',
          userVerification: "required", // Ensure FaceID/TouchID usage
          allowCredentials: data.allowCredentials.map(cred => ({
            id: Uint8Array.from(
              atob(cred.id.replace(/-/g, '+').replace(/_/g, '/')), 
              c => c.charCodeAt(0)
            ),
            type: 'public-key',
            transports: ['internal'] // 🔹 Enforce built-in auth (no USB)
          }))
        }
      });
  
      // Convert response to base64URL format
      const assertionResponse = {
        id: assertion.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(assertion.rawId)))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=/g, ''),
        response: {
          authenticatorData: btoa(String.fromCharCode(...new Uint8Array(assertion.response.authenticatorData)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, ''),
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(assertion.response.clientDataJSON)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, ''),
          signature: btoa(String.fromCharCode(...new Uint8Array(assertion.response.signature)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '')
        },
        type: assertion.type
      };
  
      // Send authentication result to backend
      const verifyResponse = await fetch(`${API_URL}/auth/webauthn/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, assertionResponse })
      });
  
      if (!verifyResponse.ok) {
        const data = await verifyResponse.json();
        throw new Error(data.error || 'WebAuthn login failed');
      }
  
      const { token: authToken, user: userData } = await verifyResponse.json();
  
      // Store authentication details
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
  
      navigate(userData.role === 'user' ? '/employee_forms' : '/dashboard');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // 🔹 UI Rendering Logic
  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4, px: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" align="center" gutterBottom>
          First Time Setup
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 📌 Step 1: Enter Email to Request Token */}
        {step === 'email' && (
          <form onSubmit={requestToken}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Request Token'}
            </Button>
          </form>
        )}

        {/* 📌 Step 2: Enter Token to Verify */}
        {step === 'token' && (
          <form onSubmit={verifyToken}>
            <TextField
              fullWidth
              label="Enter Token"
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              margin="normal"
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify Token'}
            </Button>
          </form>
        )}

        {/* 📌 Step 3: WebAuthn Login */}
        {step === 'webauthn' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>
              Setting up secure authentication...
            </Typography>
          </Box>
        )}

        {/* 🔹 Add WebAuthn Login Button */}
        {step === 'email' && (
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            onClick={handleWebAuthnLogin}
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Use WebAuthn to Login'}
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default FirstTimeSetup;